import {useEffect} from 'react'
import {useLocation, useHistory} from 'react-router-dom'
import {
    SET_CIO_SELECTED_FACETS,
    SET_SELECTED_SORT_OPTION,
    SET_CIO_FACETS_INITIALIZED,
    CioFacetState,
    CioFacetAction
} from '@/context/cio-facet'
import {isEditing, isPreviewing} from '@builder.io/sdk-react'

type SortOption = CioFacetState['sortOptions'][0]
type SelectedCioFacets = CioFacetState['selectedCioFacets']

const appendParamsForSortOptions = (params: URLSearchParams, sortOption: SortOption) => {
    const sortMap: Record<string, string> = {
        'price-ascending': 'price-low-to-high',
        'price-descending': 'price-high-to-low',
        'relevance-descending': 'best-sellers',
        'averageRating-descending': 'ratings',
        'item_name-ascending': 'product-name-a-z',
        'item_name-descending': 'product-name-z-a'
    }

    const key = `${sortOption.sort_by}-${sortOption.sort_order}`
    const srule = sortMap[key] || null
    srule && params.set('srule', srule)
}

const appendParamsForSelectedCioFacets = (
    params: URLSearchParams,
    selectedCioFacets: SelectedCioFacets
) => {
    Object.entries(selectedCioFacets).forEach(([key, values]) => {
        if (key === 'apiKey') return
        const isPriceValues = values?.length > 0
        if (key === 'price' && isPriceValues) {
            const [min, max] = values[0].value.split('-')
            params.set('pmax', max.trim().replace(/"/g, ''))
            params.set('pmin', min.trim().replace(/"/g, ''))
        } else {
            values.forEach((item) => {
                if (params.has(key)) {
                    let paramValue = params.get(key) ?? ''
                    paramValue = paramValue.concat(`,${item.value}`)
                    params.set(key, paramValue)
                } else {
                    params.append(key, item.value)
                }
            })
        }
    })
}

const buildQueryString = (
    selectedCioFacets: SelectedCioFacets,
    selectedSortOption?: SortOption | null
) => {
    const isSortOptionSelected =
        selectedSortOption && selectedSortOption.sort_by && selectedSortOption.sort_order
    const isCioFacetsSelected = Object.keys(selectedCioFacets).length > 0
    const params = new URLSearchParams()

    isCioFacetsSelected && appendParamsForSelectedCioFacets(params, selectedCioFacets)
    isSortOptionSelected && appendParamsForSortOptions(params, selectedSortOption)

    return params.toString()
}

function parseFacetsAndSortFromUrl(search: string) {
    const isEdit = isEditing()
    const isPreview = isPreviewing()
    const params = new URLSearchParams(search)
    const facets: SelectedCioFacets = {}
    let sortOption: SortOption | null = null

    // Parse price
    const pmin = params.get('pmin')
    const pmax = params.get('pmax')
    if (pmin || pmax) {
        facets.price = [
            {
                value: `${pmin || ''} - ${pmax || ''}`,
                display_name: `${pmin || ''} - ${pmax || ''}`
            }
        ]
    }

    // Parse other facets
    params.forEach((value, key) => {
        switch (true) {
            case key === 'pmin' || key === 'pmax':
                facets.price = [
                    {
                        value: `${pmin || ''} - ${pmax || ''}`,
                        display_name: `${pmin || ''} - ${pmax || ''}`
                    }
                ]
                return
            case key === 'srule' || key === 'page':
                return
            case (isEdit || isPreview) && key.includes('builder'):
                return
            default:
                facets[key] = value.split(',').map((v) => ({
                    value: v,
                    display_name: v
                }))
        }
    })

    // Parse sort option
    const srule = params.get('srule')
    if (srule) {
        // Map srule back to your sortOption object as needed
        const sortMap: Record<string, SortOption> = {
            'price-low-to-high': {
                sort_by: 'price',
                display_name: 'Price Low to High',
                sort_order: 'ascending'
            },
            'price-high-to-low': {
                sort_by: 'price',
                display_name: 'Price High to Low',
                sort_order: 'descending'
            },
            'best-sellers': {
                sort_by: 'relevance',
                display_name: 'Best Sellers',
                sort_order: 'descending'
            },
            ratings: {sort_by: 'averageRating', display_name: 'Ratings', sort_order: 'descending'},
            'product-name-a-z': {
                sort_by: 'item_name',
                display_name: 'Product Name A-Z',
                sort_order: 'ascending'
            },
            'product-name-z-a': {
                sort_by: 'item_name',
                display_name: 'Product Name Z-A',
                sort_order: 'descending'
            }
        }
        sortOption = sortMap[srule] || null
    }

    return {facets, sortOption}
}

// Utility: Compare two URLSearchParams for equality
const areParamsEqual = (a: URLSearchParams, b: URLSearchParams) => {
    if (a.toString() === b.toString()) return true
    const aKeys = Array.from(a.keys())
    const bKeys = Array.from(b.keys())
    if (aKeys.length !== bKeys.length) return false
    return aKeys.every((key) => a.get(key) === b.get(key))
}

export const useSyncCioFacetsToUrl = (
    state: CioFacetState,
    dispatch: React.Dispatch<CioFacetAction>
) => {
    const location = useLocation() as {pathname: string; search: string}
    const isUrlWithParams = location.search?.split('&').length > 0
    const history = useHistory() // or your context

    // On mount: sync context from URL if context is empty
    useEffect(() => {
        if (isUrlWithParams) {
            const {facets, sortOption} = parseFacetsAndSortFromUrl(location.search)
            if (Object.keys(facets).length > 0) {
                dispatch({type: SET_CIO_SELECTED_FACETS, payload: facets})
            }
            if (sortOption) {
                dispatch({type: SET_SELECTED_SORT_OPTION, payload: sortOption})
            }
        }
        dispatch({type: SET_CIO_FACETS_INITIALIZED})
    }, [dispatch, isUrlWithParams, location.search])

    // React to state changes in a separate effect
    useEffect(() => {
        const hasSelectedFacets = Object.keys(state.selectedCioFacets).length > 0
        const hasSelectedSort = state.selectedSortOption && state.selectedSortOption.sort_by
        if (hasSelectedFacets || hasSelectedSort) {
            const newFacetParams = new URLSearchParams(
                buildQueryString(state.selectedCioFacets, state.selectedSortOption)
            )
            const currentUrlParams = new URLSearchParams(location.search)
            if (currentUrlParams.has('page')) {
                newFacetParams.set('page', currentUrlParams.get('page') || '1')
            }
            const shouldUpdate = !areParamsEqual(currentUrlParams, newFacetParams)
            const url = shouldUpdate
                ? `${location.pathname}?${newFacetParams.toString()}`
                : undefined
            if (shouldUpdate && url) {
                history.replace(url)
            }
        }
    }, [
        state.selectedCioFacets,
        state.selectedSortOption,
        location.pathname,
        location.search,
        history,
        isUrlWithParams
    ])
}
